import React, { Component, useRef, useState, useEffect } from 'react';
import {Link, useNavigate, useParams } from "react-router-dom";
import NavBarMini from './NavBarMini.js';
import Footer from './Footer.js';
import Announcement from './Announcement';
import LoadingAnimation from './LoadingAnimation.js';
import * as utils from '../common/Utils.js'
import { getAuth, onAuthStateChanged } from "firebase/auth";

import { collection, query, where, getDoc, getDocs, setDoc, updateDoc, doc, Timestamp } from "firebase/firestore";
import { db } from '../firebase.js';
import { useLeagueData } from '../contexts/LeagueDataContext.js';
import { useConfig } from '../contexts/ConfigContext.js';

const auth = getAuth();
const user = auth.currentUser

let DATE_WHEN_BLOCKS_AND_STEALS_WERE_ADDED = new Date('May 27, 2024');
let SHOW_BLOCKS_AND_STEALS = true;

let defaultPlayer = {
  jerseyNumber: '',
  id: '0'
}


const Game = () => {
  const navigate = useNavigate();
  const { gameID, id } = useParams();
  const { teams: contextTeams, seasonTeams: contextSeasonTeams, players: contextPlayers, games: contextGames, dataLoaded } = useLeagueData(['teams', 'seasonTeams', 'players', 'games']);
  const config = useConfig();
  const SEASON = config.currentSeason;

  const [league, setLeague] = useState({ games: [], players: [], teams: [] });
  const [game, setGame] = useState({})
  const [awayTeam, setAwayTeam] = useState({ id: null, name: 'TBD', players: [], seed: '' })
  const [homeTeam, setHomeTeam] = useState({ id: null, name: 'TBD', players: [], seed: '' })
  const [gameStats, setGameStats] = useState([])
  const [allGameStats, setAllGameStats] = useState([])
  const [allGames, setAllGames] = useState([])
  const [games, setGames] = useState([])
  const [teams, setTeams] = useState([])
  const [players, setPlayers] = useState([])
  const [playerCareerRecords, setPlayerCareerRecords] = useState({})
  const [dataReady, setDataReady] = useState(false);
  const [gameStatsLoaded, setGameStatsLoaded] = useState(false);
  const [recordsLoaded, setRecordsLoaded] = useState(false);
  const [currentUser, setCurrentUser] = useState({})
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [comments, setComments] = useState({ posts: [], replies: [] });
  const [article, setArticle] = useState({});
  const [records, setRecords] = useState({
    league: { points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0 },
    season: { points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0 }
  });

  // Auth: handle sign-in state + fetch comments/article/this game's stats
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setCurrentUser(currentUser);
        getCurrentPlayer(currentUser.email);
      } else {
        setCurrentUser(null);
      }
      getComments();
      getArticle();
      fetchGameStats();
    });
    return () => unsubscribe();
  }, []);

  // Populate game/teams/players/allGames from context once loaded
  useEffect(() => {
    if (!dataLoaded) return;

    const foundGame = contextGames.find(g => g.id == gameID);
    if (!foundGame) {
      navigate('/games');
      return;
    }
    setGame(foundGame);
    setAllGames(contextGames);
    setGames(contextGames.filter(g => g.season == parseInt(SEASON)));
    setLeague({ games: contextGames, players: contextPlayers, teams: contextTeams });

    const mergedTeams = contextTeams.map(team => {
      const seasonTeam = contextSeasonTeams.find(st => st.teamID === team.id && st.season === SEASON);
      return { ...seasonTeam, ...team };
    });
    setTeams(mergedTeams);
    setPlayers(contextPlayers);

    const showPlaceholders = foundGame.season == SEASON && foundGame.type === 'playoff' && !config.showPlayoffBracket;
    if (!showPlaceholders) {
      if (foundGame.awayTeamID) {
        setAwayTeam(utils.getTeamForID(mergedTeams, foundGame.awayTeamID));
      }
      if (foundGame.homeTeamID) {
        setHomeTeam(utils.getTeamForID(mergedTeams, foundGame.homeTeamID));
      }
    }
  }, [dataLoaded, contextGames, contextTeams, contextSeasonTeams, contextPlayers]);

  // Fetch league/season records once we know game.season
  useEffect(() => {
    if (!game.season) return;
    const emptyRecord = { points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0 };
    Promise.all([
      getDoc(doc(db, 'records', 'league')),
      getDoc(doc(db, 'records', `season_${game.season}`))
    ]).then(([leagueSnap, seasonSnap]) => {
      setRecords({
        league: leagueSnap.exists() ? leagueSnap.data() : emptyRecord,
        season: seasonSnap.exists() ? seasonSnap.data() : emptyRecord
      });
      setRecordsLoaded(true);
    });
  }, [game.season]);

  // Fetch career records per player for box score stat decoration
  useEffect(() => {
    if (gameStats.length === 0) return;
    const playerIDs = [...new Set(gameStats.map(s => s.playerID))];
    Promise.all(playerIDs.map(pid => getDoc(doc(db, 'records', pid)))).then(snaps => {
      const map = {};
      playerIDs.forEach((pid, i) => {
        map[pid] = snaps[i].exists() ? snaps[i].data() : {};
      });
      setPlayerCareerRecords(map);
    });
  }, [gameStats]);

  // Fetch all game stats for season/all-time averages tables (non-blocking).
  // Only needed for upcoming games (showTopSeasonStats) -- completed games get
  // their box-score "career high" decoration from the precomputed `records` docs above.
  useEffect(() => {
    if (!dataLoaded || contextGames.length === 0) return;
    if (!game.id || game.winner) return;
    const gameIDs = contextGames.map(g => g.id);
    Promise.all(gameIDs.map(id => getDoc(doc(db, 'gameStats', id)))).then(snaps => {
      const all = snaps.flatMap(snap => snap.exists() ? (snap.data().stats || []) : []);
      setAllGameStats(all);
    });
  }, [dataLoaded, contextGames, game.id, game.winner]);

  // dataReady when context + this game's stats + records are all loaded
  useEffect(() => {
    if (dataLoaded && gameStatsLoaded && recordsLoaded) {
      setDataReady(true);
    }
  }, [dataLoaded, gameStatsLoaded, recordsLoaded]);

  const getComments = async () => {
    // Move to new format after games is converted in DB
    const commentsSnapshot = await getDocs(collection(db, "games", gameID, "comments"));
    const comments = []
    commentsSnapshot.forEach((doc) => {
      const id = doc.id
      const comment = doc.data()
      comments.push(comment)
    });

    const publishedComments = comments.filter((c) => c.status == 'published');

    const posts = publishedComments.filter((c) => c.replyTo == '');
    const replies = publishedComments.filter((c) => c.replyTo !== '');

    const commentsObject = {
      posts: posts,
      replies: replies
    }

    setComments(commentsObject);
  }

  const getArticle = async () => {
    const articleRef = doc(db, "articles", gameID);
    const docSnap = await getDoc(articleRef);

    if (docSnap.exists()) {
      const article = docSnap.data();

      setArticle(article);
    } else {
      setArticle(null);
    }
  };

  const getCurrentPlayer = async (email) => {
    const q = query(collection(db, 'players'), where("email", "==", email));
    const snapshot = await getDocs(q);
    const player = snapshot.docs[0]?.data();
    setCurrentPlayer(player);
  }

  const fetchGameStats = async () => {
    const snap = await getDoc(doc(db, 'gameStats', gameID));
    const stats = snap.exists() ? (snap.data().stats || []).filter(s => s.gameID == gameID) : [];
    setGameStats(stats);
    setGameStatsLoaded(true);
  }


  function createNewIDForComment () {
    let dateString = Date.now().toString(36);
    let randomness = Math.random().toString(36).substr(2);
    let commentID = dateString + randomness;

    return commentID;
  }

  function addReply (commentID) {
    const commentFieldElement = document.getElementById(`replyGroup-${commentID}`);
    commentFieldElement.classList.remove('hidden')

    const replyButtonElement = document.getElementById(`replyButtonElement-${commentID}`);
    replyButtonElement.classList.add('hidden')
  }

  function addComment () {
    const commentFieldElement = document.getElementById('commentField');
    commentFieldElement.classList.remove('hidden')

    const cancelButtonElement = document.getElementById('cancelCommentButton');
    cancelButtonElement.classList.remove('hidden')

    const addButtonElement = document.getElementById('addCommentButton');
    addButtonElement.classList.add('hidden')

    const postButtonElement = document.getElementById('postCommentButton');
    postButtonElement.classList.remove('hidden')
  }

  async function createComment(comment) {
    try {
      // Move to new format after games is converted in DB
      const commentRef = doc(db, "comments", comment.id);
      const gameCommentRef = doc(db, "games", gameID, "comments", comment.id);
      await setDoc(commentRef, comment);
      await setDoc(gameCommentRef, comment);
      addReplyToComment(comment)
    } catch (e) {
      console.error("Error adding document: ", e);
    }
  }

  function addReplyToComment (comment) {
    let parentCommentDiv;
    let parentComment;

    // Create the comment container
    const commentDiv = document.createElement("div");
    commentDiv.id = `comment-${comment.id}`;

    if (comment.replyTo == '') {
      parentCommentDiv = document.getElementById(`allCommentsGroup`)
      parentComment = document.getElementById(`comment`)
      commentDiv.className = `league-game-comment reply-0`;
    } else {
      parentCommentDiv = document.getElementById(`commentParent-${comment.replyTo}`)
      parentComment = document.getElementById(`comment-${comment.replyTo}`)
      let parentClasses = Array.from(parentComment.classList)
      let replyIndentation = parentClasses.find((c) => c.startsWith('reply-'))
      let replyIndentationValue = replyIndentation.split('-')[1]
      commentDiv.className = `league-game-comment reply-${parseInt(replyIndentationValue) + 1} league-game-comment-reply`;
    }

    // Create the image div
    const imageDiv = document.createElement("div");
    imageDiv.className = "league-commenter-image-mini commenter-image";

    const initials = document.createElement("p");
    initials.className = "commenter-image-initials";
    initials.textContent = `${comment.author.firstName[0]}${comment.author.lastName[0]}`;
    imageDiv.appendChild(initials);

    // Create author and text container
    const authorTextDiv = document.createElement("div");
    authorTextDiv.className = "league-game-comment-author-and-text";

    const authorName = document.createElement("p");
    authorName.className = "league-comment-author-name";
    authorName.innerText = `${comment.author.firstName} ${comment.author.lastName}`;
    authorTextDiv.appendChild(authorName);

    const commentDate = document.createElement("label");
    commentDate.className = "league-comment-date";

    commentDate.innerText = getTimeAgo(comment.timestamp);
    authorTextDiv.appendChild(commentDate);

    const commentText = document.createElement("p");
    commentText.className = "league-comment-text";
    commentText.innerText = comment.text;
    authorTextDiv.appendChild(commentText);

    const replyButton = document.createElement("p");
    replyButton.id = `replyButtonElement-${comment.id}`;
    replyButton.className = "league-comment-reply";
    replyButton.innerText = "Reply";
    replyButton.onclick = () => addReply(comment.id);
    authorTextDiv.appendChild(replyButton);

    commentDiv.appendChild(imageDiv);
    commentDiv.appendChild(authorTextDiv);

    // Create the reply group
    const replyGroup = document.createElement("div");
    replyGroup.id = `replyGroup-${comment.id}`;
    replyGroup.className = "hidden";

    const loginBody = document.createElement("div");
    loginBody.className = "login-body";

    const replyField = document.createElement("div");
    replyField.id = `replyField-${comment.id}`;
    replyField.className = "login-field";

    const replyLabel = document.createElement("label");
    replyLabel.textContent = "Add a reply";
    replyField.appendChild(replyLabel);

    const formGroup = document.createElement("div");
    formGroup.className = "form-group";

    const inputGroup = document.createElement("div");
    inputGroup.id = `commentInputGroup-${comment.id}`;
    inputGroup.className = "login-input-group";

    const inputField = document.createElement("input");
    inputField.type = "text";
    inputField.id = `commentInput-${comment.id}`;
    inputGroup.appendChild(inputField);

    formGroup.appendChild(inputGroup);
    replyField.appendChild(formGroup);

    const replyErrorLabel = document.createElement("label");
    replyErrorLabel.id = `replyInputFieldError-${comment.id}`;
    replyField.appendChild(replyErrorLabel);

    loginBody.appendChild(replyField);

    // Add buttons
    const buttonGroup = document.createElement("div");
    buttonGroup.className = "comment-btn-grp";

    const postButton = document.createElement("button");
    postButton.id = `postReplyButton-${comment.id}`;
    postButton.className = "login-btn";
    postButton.type = "submit";
    postButton.textContent = "Post";
    postButton.onclick = () => postReply(comment.id);
    buttonGroup.appendChild(postButton);

    const cancelButton = document.createElement("button");
    cancelButton.id = `cancelReplyButton-${comment.id}`;
    cancelButton.className = "login-btn-secondary";
    cancelButton.type = "submit";
    cancelButton.textContent = "Cancel";
    cancelButton.onclick = () => cancelReply(comment.id);
    buttonGroup.appendChild(cancelButton);

    loginBody.appendChild(buttonGroup);
    replyGroup.appendChild(loginBody);

    const replyParent = document.createElement("div");
    replyParent.id = `commentParent-${comment.id}`;
    replyParent.key = comment.id;

    if (comment.replyTo == '') {
      const hrElement = document.createElement("hr");
      replyParent.appendChild(hrElement)
    }

    replyParent.appendChild(commentDiv)
    replyParent.appendChild(replyGroup)


    if (comment.replyTo == '') {
      parentCommentDiv.insertBefore(replyParent, parentCommentDiv.firstChild);
    } else {
      const allReplies = document.getElementById(`allReplies-${comment.replyTo}`)

      allReplies.insertBefore(replyParent, allReplies.firstChild)
    }
  }

  function showSpinner(spinnerID) {
    const replySpinner = document.getElementById(spinnerID)
    replySpinner.classList.remove('hidden')
  }
  function hideSpinner(spinnerID) {
    const replySpinner = document.getElementById(spinnerID)
    replySpinner.classList.add('hidden')
  }

  function postReply (commentID) {
    const spinnerID = `replySpinner-${commentID}`
    showSpinner(spinnerID)

    const commentFieldElement = document.getElementById(`replyGroup-${commentID}`);
    commentFieldElement.classList.add('hidden')

    const commentInput = document.getElementById(`commentInput-${commentID}`);

    if (commentInput.value == '') {
      return;
    }

    const newCommentID = createNewIDForComment()
    const datetime = Date.now()
    const dateObject = new Date(datetime)
    const timestamp = Timestamp.fromDate(dateObject)
    const comment = {
      status: 'published',
      timestamp: timestamp,
      text: commentInput.value,
      author: {
        firstName: currentPlayer.firstName,
        lastName: currentPlayer.lastName
      },
      replyTo: commentID,
      id: newCommentID,
      gameID: gameID,
      playerID: currentPlayer.id,
      likes: []
    }

    commentInput.value = ''

    createComment(comment)
    .then(() => {
      hideSpinner(spinnerID)

      const replyButtonElement = document.getElementById(`replyButtonElement-${commentID}`);
      replyButtonElement.classList.remove('hidden')
    }).catch((error) => {
      console.log(error.code)
      console.log(error.message)
    })
  }

  function postComment () {
    const spinnerID = 'commentSpinner'
    showSpinner(spinnerID)

    const commentFieldElement = document.getElementById('commentField');
    commentFieldElement.classList.add('hidden')

    const cancelButtonElement = document.getElementById('cancelCommentButton');
    cancelButtonElement.classList.add('hidden')

    const postButtonElement = document.getElementById('postCommentButton');
    postButtonElement.classList.add('hidden')

    const commentInput = document.getElementById('commentInput');

    if (commentInput.value == '') {
      return;
    }

    const newCommentID = createNewIDForComment()
    const datetime = Date.now()
    const dateObject = new Date(datetime)
    const timestamp = Timestamp.fromDate(dateObject)
    const comment = {
      status: 'published',
      timestamp: timestamp,
      text: commentInput.value,
      author: {
        firstName: currentPlayer.firstName,
        lastName: currentPlayer.lastName
      },
      replyTo: '',
      id: newCommentID,
      gameID: gameID,
      playerID: currentPlayer.id,
      likes: []
    }

    commentInput.value = ''

    createComment(comment)
    .then(() => {
      hideSpinner(spinnerID)

      const noCommentsText = document.getElementById('noCommentsText')
      noCommentsText.classList.add('hidden')

      const addButtonElement = document.getElementById('addCommentButton');
      addButtonElement.classList.remove('hidden')
    }).catch((error) => {
      console.log(error.code)
      console.log(error.message)

      hideSpinner(spinnerID)

      const addButtonElement = document.getElementById('addCommentButton');
      addButtonElement.classList.remove('hidden')
    })
  }

  function cancelReply (commentID) {
    const commentFieldElement = document.getElementById(`replyGroup-${commentID}`);
    commentFieldElement.classList.add('hidden')

    const replyButtonElement = document.getElementById(`replyButtonElement-${commentID}`);
    replyButtonElement.classList.remove('hidden')

    const commentInput = document.getElementById(`commentInput-${commentID}`);
    commentInput.value = ''
  }

  function cancelComment () {
    const commentFieldElement = document.getElementById('commentField');
    commentFieldElement.classList.add('hidden')

    const cancelButtonElement = document.getElementById('cancelCommentButton');
    cancelButtonElement.classList.add('hidden')

    const addButtonElement = document.getElementById('addCommentButton');
    addButtonElement.classList.remove('hidden')

    const postButtonElement = document.getElementById('postCommentButton');
    postButtonElement.classList.add('hidden')

    const commentInput = document.getElementById('commentInput');
    commentInput.value = ''
  }

  function displayPlayerInputRowsForTeam(stat) {
    let player = players.find((player) => player.id == stat.playerID)

    let maxPlayerPoints;
    let maxPlayerRebounds;
    let maxPlayerAssists;
    let maxPlayerSteals;
    let maxPlayerBlocks;

    let decoratorClasses = {
      'points': [],
      'rebounds': [],
      'assists': [],
      'steals': [],
      'blocks': []
    };

    let playerTeam = utils.getCurrentTeamForPlayerID(teams, stat.playerID)
    if (!playerTeam) {
      playerTeam = {
        'id': stat.teamID,
        'name': 'No Name'
      }
    }

    if (!player) {
      let playerID = stat.playerID
      player = defaultPlayer;
      player['id'] = playerID
      player['firstName'] = playerID
      player['lastName'] = ''
    } else {
      let careerRecord = playerCareerRecords[player.id] || {}
      maxPlayerPoints = careerRecord.points || 0
      maxPlayerRebounds = careerRecord.rebounds || 0
      maxPlayerAssists = careerRecord.assists || 0
      maxPlayerSteals = careerRecord.steals || 0
      maxPlayerBlocks = careerRecord.blocks || 0

      if (stat.points) {
        if (stat.points >= maxPlayerPoints) {
          if (stat.points >= records.league.points) {
            decoratorClasses.points.push('league-high-stat')
          } else if (stat.points >= records.season.points) {
            decoratorClasses.points.push('season-high-stat')
          } else {
            decoratorClasses.points.push('career-high-stat')
          }
        } else if (stat.points >= records.season.points) {
          decoratorClasses.points.push('season-high-stat')
        }
      }
      if (stat.rebounds) {
        if (stat.rebounds >= maxPlayerRebounds) {
          if (stat.rebounds >= records.league.rebounds) {
            decoratorClasses.rebounds.push('league-high-stat')
          } else if (stat.rebounds >= records.season.rebounds) {
            decoratorClasses.rebounds.push('season-high-stat')
          } else {
            decoratorClasses.rebounds.push('career-high-stat')
          }
        } else if (stat.rebounds >= records.season.rebounds) {
          decoratorClasses.rebounds.push('season-high-stat')
        }
      }
      if (stat.assists) {
        if (stat.assists >= maxPlayerAssists) {
          if (stat.assists >= records.league.assists) {
            decoratorClasses.assists.push('league-high-stat')
          } else if (stat.assists >= records.season.assists) {
            decoratorClasses.assists.push('season-high-stat')
          } else {
            decoratorClasses.assists.push('career-high-stat')
          }
        } else if (stat.assists >= records.season.assists) {
          decoratorClasses.assists.push('season-high-stat')
        }
      }
      if (stat.steals) {
        if (stat.steals >= maxPlayerSteals) {
          if (stat.steals >= records.league.steals) {
            decoratorClasses.steals.push('league-high-stat')
          } else if (stat.steals >= records.season.steals) {
            decoratorClasses.steals.push('season-high-stat')
          } else {
            decoratorClasses.steals.push('career-high-stat')
          }
        } else if (stat.steals >= records.season.steals) {
          decoratorClasses.steals.push('season-high-stat')
        }
      }
      if (stat.blocks) {
        if (stat.blocks >= maxPlayerBlocks) {
          if (stat.blocks >= records.league.blocks) {
            decoratorClasses.blocks.push('league-high-stat')
          } else if (stat.blocks >= records.season.blocks) {
            decoratorClasses.blocks.push('season-high-stat')
          } else {
            decoratorClasses.blocks.push('career-high-stat')
          }
        } else if (stat.blocks >= records.season.blocks) {
          decoratorClasses.blocks.push('season-high-stat')
        }
      }
    }

    if (stat.status == 'dnp') {
      return (
        <>
          <tr>
            <th className="league-table-data-name no-wrap" scope="row"><a className="unstyled-link" href={"/players/" + player.id }>{ player.jerseyNumber != '' ? '#' : '' }{ player.jerseyNumber } { utils.displayPlayerName(player) }</a></th>
            <td className="league-table-data-name stat-line dnp" colSpan="5">
              <p id={ getIDForPlayerAndStat(player, 'dnp') }>DNP</p>
            </td>
          </tr>
        </>
      )
    } else if (stat.status == 'played') {
      return (
        <>
          <tr>
            <th className="league-table-data-name no-wrap" scope="row"><a className="unstyled-link" href={"/players/" + player.id }>{ player.jerseyNumber != '' ? '#' : '' }{ player.jerseyNumber } { utils.displayPlayerName(player) }</a>{ playerTeam.id != stat.teamID && utils.getGameForID(allGames, stat.gameID).season == SEASON ? '*' : ''}</th>
            <td className={ "league-table-data-name stat-line " + decoratorClasses.points.join('')}>
              <p id={ getIDForPlayerAndStat(player, 'points') }>{ stat.points }</p>
            </td>
            <td className={ "league-table-data-name stat-line " + decoratorClasses.rebounds.join('')}>
              <p id={ getIDForPlayerAndStat(player, 'rebounds') }>{ stat.rebounds }</p>
            </td>
            <td className={ "league-table-data-name stat-line " + decoratorClasses.assists.join('')}>
              <p id={ getIDForPlayerAndStat(player, 'assists') }>{ stat.assists }</p>
            </td>
            { SHOW_BLOCKS_AND_STEALS ? <>
              <td className={ "league-table-data-name stat-line " + decoratorClasses.steals.join('')}>
                <p id={ getIDForPlayerAndStat(player, 'steals') }>{ stat.steals }</p>
              </td>
              <td className={ "league-table-data-name stat-line " + decoratorClasses.blocks.join('')}>
                <p id={ getIDForPlayerAndStat(player, 'blocks') }>{ stat.blocks }</p>
              </td>
            </> : '' }
          </tr>
        </>
      )
    }
  }

  function displayPlayerInputRows() {
    let awayTeamPlayerStats = gameStats.filter((stat) => stat.teamID == awayTeam.id)

    let awayTeamPlayerStatsSorted = awayTeamPlayerStats.sort((a, b) => {
      let playerA = players.find((player) => player.id == a.playerID)
      let playerB = players.find((player) => player.id == b.playerID)

      return jerseyNumberSort(playerA, playerB)
    })
    let awayTeamPlayerRows = awayTeamPlayerStatsSorted.map((stat, place) => displayPlayerInputRowsForTeam(stat))

    let homeTeamPlayerStats = gameStats.filter((stat) => stat.teamID == homeTeam.id)
    let homeTeamPlayerStatsSorted = homeTeamPlayerStats.sort((a, b) => {
      let playerA = players.find((player) => player.id == a.playerID)
      let playerB = players.find((player) => player.id == b.playerID)

      return jerseyNumberSort(playerA, playerB)
    })
    let homeTeamPlayerRows = homeTeamPlayerStatsSorted.map((stat, place) => displayPlayerInputRowsForTeam(stat))

    return (
      <>
        <tr>
          <th className="league-table-heading-name" colSpan="6"><span>{ awayTeam.name.toUpperCase() }</span></th>
        </tr>
        { awayTeamPlayerRows }
        <tr>
          <th className="league-table-heading-name" colSpan="6"><span>{ homeTeam.name.toUpperCase() }</span></th>
        </tr>
        { homeTeamPlayerRows }
      </>
    )
  }

  function jerseyNumberSort (a, b) {
    if (!a && !b) {
      return 0;
    } else if (!a) {
      return 1;
    } else if (!b) {
      return -1;
    } else if (a.jerseyNumber == '' && b.jerseyNumber == '') {
      return 0;
    } else if (a.jerseyNumber == '') {
      return 1;
    } else if (b.jerseyNumber == '') {
      return -1;
    } else {
      let number1 = parseInt(a.jerseyNumber)
      let number2 = parseInt(b.jerseyNumber)

      return number1 < number2 ? -1 : 1
    }
  }

  function getIDForPlayerAndStat(player, stat) {
    return `${player.id}-${stat}`
  }

  function displayGame () {
    if (game.date < DATE_WHEN_BLOCKS_AND_STEALS_WERE_ADDED) {
      SHOW_BLOCKS_AND_STEALS = false;
    } else {
      SHOW_BLOCKS_AND_STEALS = true;
    }

    if (game.winner) {
      return displayCompletedGame();
    } else {
      return displayFutureGame();
    }
  }

  function displayFutureGame () {
    let awayTeamTBD = false;
    let homeTeamTBD = false;

    let showPlaceholders = game.type == 'playoff' && !config.showPlayoffBracket
    if (!awayTeam || showPlaceholders) {
      awayTeamTBD = true;
    }

    if (!homeTeam  || showPlaceholders) {
      homeTeamTBD = true;
    }

    let hours = game.date.getHours();
    let hoursLabel = 'AM'
    if(hours >= 12){
      hoursLabel = 'PM';
    }

    if(hours > 12){
      hours -= 12;
    }
    if(hours == 0){
      hours = 12;
    }

    return (
      <>
        <div className='league-game-display'>
          <div className='text-left'>
            <p className="league-title-standard-small">
              {awayTeam.id ? (
                <a className="unstyled-link" href={`/teams/${awayTeam.id}`}>
                  <b>{awayTeam.name.toUpperCase()}</b>
                </a>
              ) : (
                <b>{awayTeam.name.toUpperCase()}</b>
              )}
            </p>
          </div>

          <div className='text-left'>
            <p className="league-title-standard-small">
              {homeTeam.id ? (
                <a className="unstyled-link" href={`/teams/${homeTeam.id}`}>
                  <b>{homeTeam.name.toUpperCase()}</b>
                </a>
              ) : (
                <b>{homeTeam.name.toUpperCase()}</b>
              )}
            </p>
          </div>
        </div>


        <div className='league-game-display'>
          <div className='text-left'>
            <p className='league-title-standard league-team-record' key={gameID + '-away-record'}>{ utils.getRecordForID(games, game.awayTeamID) }</p>
          </div>

          <div className='text-center margin-top-20'>
            {
              game.status === 'postponed' ?
              <>
                <p className='league-title-standard-small'>Postponed</p>
                <p className='league-title-standard-small'>{ utils.abbvWeekday(game.date.getDay()) }, { game.date.getMonth() + 1 }/{ game.date.getDate() }</p>
              </>
              :
              game.status === 'cancelled' ?
              <>
                <p className='league-title-standard-small'>Cancelled</p>
                <p className='league-title-standard-small'>{ utils.abbvWeekday(game.date.getDay()) }, { game.date.getMonth() + 1 }/{ game.date.getDate() }</p>
              </>
              :
              <>
                <p className='league-title-standard-small text-center'>{ utils.abbvWeekday(game.date.getDay()) }, { game.date.getMonth() + 1 }/{ game.date.getDate() }</p>
                <p className='league-game-location text-center'>{ hours }:{ game.date.getMinutes().toString().padStart(2, '0') } { hoursLabel }</p>
                <p className='league-game-location text-center'>{ utils.getLocationName(game.location) }</p>
              </>
            }
          </div>

          <div className='text-right'>
            <p className='league-title-standard league-team-record' key={gameID + '-home-record'}>{ utils.getRecordForID(games, game.homeTeamID) }</p>
          </div>
        </div>

        { game.status === 'cancelled' || awayTeamTBD || homeTeamTBD ? '' : <>
          { games.filter(g => g.status == 'completed').length > 0 ?
          <div className="league-game-stats-container">
            <p className="league-title-skinny-medium"><b>Season Leaders</b></p>
            { showTopSeasonStats(game, awayTeam, homeTeam, 'points', SEASON) }
            { showTopSeasonStats(game, awayTeam, homeTeam, 'rebounds', SEASON) }
            { showTopSeasonStats(game, awayTeam, homeTeam, 'assists', SEASON) }
            { showTopSeasonStats(game, awayTeam, homeTeam, 'steals', SEASON) }
            { showTopSeasonStats(game, awayTeam, homeTeam, 'blocks', SEASON) }
          </div>
          : <></> }

          <div className="league-game-stats-container">
            <p className="league-title-skinny-medium"><b>All-Time Leaders</b></p>
            { showTopSeasonStats(game, awayTeam, homeTeam, 'points') }
            { showTopSeasonStats(game, awayTeam, homeTeam, 'rebounds') }
            { showTopSeasonStats(game, awayTeam, homeTeam, 'assists') }
            { showTopSeasonStats(game, awayTeam, homeTeam, 'steals') }
            { showTopSeasonStats(game, awayTeam, homeTeam, 'blocks') }
          </div>
        </> }
      </>
    )
  }

  function redirectToEditGame () {
    navigate(`/admin/games/${gameID}`)
  }

  function showEditOption () {
    if (!currentUser) {
      return
    }

    if (!config.adminEmails.includes(currentUser.email)) {
      return
    }

    return (
      <span><a onClick={ redirectToEditGame } type="button">✏️</a></span>
    )
  }

  function displayReply (reply, replyDepth) {
    return (
      <>
        <div id={ `commentParent-${reply.id}` } key={reply.id}>
          <div id={ `comment-${reply.id}` } className={ `league-game-comment reply-${replyDepth} league-game-comment-reply` }>
            <div>
              <div className="league-commenter-image-mini commenter-image">
                <p className="commenter-image-initials">{ reply.author.firstName[0] }{ reply.author.lastName[0]}</p>
              </div>
            </div>
            <div className='league-game-comment-author-and-text'>
              <p className='league-comment-author-name'>{ reply.author.firstName } { reply.author.lastName }</p>
              <label className='league-comment-date'><span>{ getTimeAgo(reply.timestamp) }</span></label>
              <p className='league-comment-text'>{ reply.text }</p>

              { currentUser && currentPlayer ? <p id={`replyButtonElement-${reply.id}`} className="league-comment-reply" onClick={() => {addReply(reply.id)} }>Reply</p> : <></> }
              <div id={`replySpinner-${reply.id}`} className="spinner-border league-spinner-primary hidden" role="status"></div>
            </div>
          </div>
          <div id={ `replyGroup-${reply.id}` } className='hidden'>
            <div className="login-body">
              <div id={ `replyField-${reply.id}` } className="login-field">
                <label>Add a reply</label>
                <div className="form-group">
                  <div id={ `commentInputGroup-${reply.id}` } className="login-input-group">
                    <input type="text" id={ `commentInput-${reply.id}` }></input>
                  </div>
                </div>
                <label id={ `replyInputFieldError-${reply.id}` }></label>
              </div>
              <div className='comment-btn-grp'>
                <button id={ `postReplyButton-${reply.id}` } className="login-btn" type="submit" onClick={() => {postReply(reply.id)}}>Post</button>
                <button id={ `cancelReplyButton-${reply.id}` } className="login-btn-secondary" type="submit" onClick={() => {cancelReply(reply.id)}}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
        { displayReplies(reply.id, comments.replies.filter((r) => r.replyTo == reply.id), replyDepth) }
      </>
    )
  }

  function displayReplies (replyTo, replies, replyDepth) {
    replyDepth += 1

    return (
      <div id={ `allReplies-${replyTo}` } className='reply-list'>
        { replies.map((reply) => displayReply(reply, replyDepth)) }
      </div>
    )
  }

  function displayComments () {
    if (comments.posts.length == 0) {
      return (
        <>
          <p id="noCommentsText">No comments</p>
        </>
      )
    } else {
      return comments.posts.map((comment) => (
        <>
          <hr></hr>
          <div id={ `commentParent-${comment.id}` } key={comment.id}>
            <div id={ `comment-${comment.id}` } className="league-game-comment reply-0">
              <div>
                <div className="league-commenter-image-mini commenter-image">
                  <p className="commenter-image-initials">{ comment.author.firstName[0] }{ comment.author.lastName[0]}</p>
                </div>
              </div>
              <div className='league-game-comment-author-and-text'>
                <p className='league-comment-author-name'>{ comment.author.firstName } { comment.author.lastName }</p>
                <label className='league-comment-date'><span>{ getTimeAgo(comment.timestamp) }</span></label>
                <p className='league-comment-text'>{ comment.text }</p>

                { currentUser && currentPlayer ? <p id={`replyButtonElement-${comment.id}`} className="league-comment-reply" onClick={() => {addReply(comment.id)} }>Reply</p> : <></> }

                <div id={`replySpinner-${comment.id}`} className="spinner-border league-spinner-primary hidden" role="status"></div>
              </div>
            </div>
            <div id={ `replyGroup-${comment.id}` } className='hidden'>
              <div className="login-body">
                <div id={ `replyField-${comment.id}` } className="login-field">
                  <label>Add a reply</label>
                  <div className="form-group">
                    <div id={ `commentInputGroup-${comment.id}` } className="login-input-group">
                      <input type="text" id={ `commentInput-${comment.id}` }></input>
                    </div>
                  </div>
                  <label id={ `replyInputFieldError-${comment.id}` }></label>
                </div>
                <div className='comment-btn-grp'>
                  <button id={ `postReplyButton-${comment.id}` } className="login-btn" type="submit" onClick={() => {postReply(comment.id)}}>Post</button>
                  <button id={ `cancelReplyButton-${comment.id}` } className="login-btn-secondary" type="submit" onClick={() => {cancelReply(comment.id)}}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
          { displayReplies(comment.id, comments.replies.filter((r) => r.replyTo == comment.id), 0) }
        </>
      ))
    }
  }

  function articleClick () {
    navigate(`/articles/${gameID}`);
  }

  function displayCompletedGame () {
    let losingTeam = game.winner === game.awayTeamID ? homeTeam : awayTeam

    // eventually abstract this out...
    let playedStats = gameStats.filter((stat) => stat.status == 'played')
    let forfeitStats = gameStats.filter((stat) => stat.status == 'forfeit')

    let statsCollected = playedStats.length > 0
    let gameForfeited = forfeitStats.length > 0

    return (
      <>
        <div className='league-game-display'>
          <div className='text-left'>
            <p className={ game.winner === game.awayTeamID ? "league-title-standard-small" : "league-title-standard-small league-game-loser" }><a className="unstyled-link" href={"/teams/" + awayTeam.id }><b>{ awayTeam.name.toUpperCase() }</b></a></p>
          </div>

          <div className='text-right'>
            <p className={ game.winner === game.homeTeamID ? "league-title-standard-small" : "league-title-standard-small league-game-loser" }><a className="unstyled-link" href={"/teams/" + homeTeam.id }><b>{ homeTeam.name.toUpperCase() }</b></a></p>
          </div>
        </div>

        <div className='league-game-display'>
          <div className='text-left'>
            <p className={ game.winner === game.awayTeamID ? "league-title-standard no-margin" : "league-title-standard no-margin league-game-loser" }><b>{ game.score.away }</b></p>
          </div>

          <div className='text-center'>
            <p className='league-game-final-label no-margin'><b>
              <span className={ game.winner == game.homeTeamID ? 'text-color-white' : '' }>← </span>
              { game?.overtime ? 'Final / OT' : 'Final' }
              <span className={ game.winner == game.awayTeamID ? 'text-color-white' : '' }> →</span>
            </b></p>
            <p className='league-title-standard-small'>{ game.date.getMonth() + 1 }/{ game.date.getDate() }/{ game.date.getFullYear() }</p>
          </div>

          <div className='text-right'>
            <p className={ game.winner === game.homeTeamID ? "league-title-standard no-margin" : "league-title-standard no-margin league-game-loser" }><b>{ game.score.home }</b></p>
          </div>
        </div>

        { article ?
          <div className='game-article-container' onClick={articleClick} style={{ cursor: 'pointer' }}>
            <p className='league-game-article-title'>{ article.title }</p>
            <div>
              { article.created_at.toDate && (new Date() - article.created_at.toDate() < 24 * 60 * 60 * 1000) && (
                <span style={{ paddingRight: 0 }}>
                  <span className="league-article-live">• LIVE</span>
                </span>
              )}

              <label className='league-article-date'><span>{ getTimeAgo(article.created_at) }</span></label>
            </div>
            <p className='league-game-article-summary'>{ article.summary }</p>
            <p className='league-link league-game-article-read-more'>READ MORE →</p>
          </div>
          : ''
        }


        {
          statsCollected ?
          <>
            <div className="league-game-stats-container">
              <p className="league-title-skinny-medium"><b>Game Leaders</b></p>
              { showTopStats(gameStats, 'points') }
              { showTopStats(gameStats, 'rebounds') }
              { showTopStats(gameStats, 'assists') }
              { showTopStats(gameStats, 'steals') }
              { showTopStats(gameStats, 'blocks') }
            </div>

            <div id="boxScore" className="league-game-stats-container">
              <Announcement />
              
              <p className="league-title-skinny-medium"><b>Box Score</b></p>
              <div className="league-title-examples">
                <p className="league-title-example career-high-stat-example"><em>Player career high</em></p>
                <p className="league-title-example season-high-stat-example"><em>Season high</em></p>
                <p className="league-title-example league-high-stat-example"><em>League record</em></p>
              </div>

              <div className="league-table-container scroll-on-overflow">
                <table className="table league-table">
                  <thead className="league-table-heading-hole">
                    <tr>
                      <th className="league-table-heading-name" scope="col"><span>Player</span></th>
                      <th className="league-table-heading hole medium-col" scope="col"><span>PTS</span></th>
                      <th className="league-table-heading hole medium-col" scope="col"><span>REB</span></th>
                      <th className="league-table-heading hole medium-col" scope="col"><span>AST</span></th>
                      { SHOW_BLOCKS_AND_STEALS ? <>
                        <th className="league-table-heading hole medium-col" scope="col"><span>STL</span></th>
                        <th className="league-table-heading hole medium-col" scope="col"><span>BLK</span></th>
                      </> : '' }
                    </tr>
                  </thead>
                  <thead className="league-table-heading-total">
                    <tr>
                      <th className="league-table-heading-name" scope="col"><span>Player</span></th>
                      <th className="league-table-heading hole small-col" scope="col"><span>pts</span></th>
                      <th className="league-table-heading hole small-col" scope="col"><span>reb</span></th>
                      <th className="league-table-heading hole small-col" scope="col"><span>ast</span></th>
                      { SHOW_BLOCKS_AND_STEALS ? <>
                        <th className="league-table-heading hole small-col" scope="col"><span>stl</span></th>
                        <th className="league-table-heading hole small-col" scope="col"><span>blk</span></th>
                      </> : '' }
                    </tr>
                  </thead>
                  <tbody>
                    { displayPlayerInputRows() }
                  </tbody>
                </table>
              </div>
            </div>
            <div id="commentSection" className="league-game-stats-container">
              <p className="league-title-skinny-medium"><b>Comments</b></p>

              <div id="commentReplyGroup">
                <div className="login-body">
                  <div id="commentField" className="login-field hidden">
                    <label>Add a comment</label>
                    <div className="form-group">
                      <div id='commentInputGroup' className="login-input-group">
                        <input type="text" id='commentInput'></input>
                      </div>
                    </div>
                    <label id="commentInputFieldError"></label>
                  </div>
                  <div className='comment-btn-grp'>
                    { currentUser && currentPlayer ?
                      <button id="addCommentButton" className="login-btn" type="submit" onClick={addComment}>Add comment</button>
                      : currentUser ?
                      <p>Only players are allowed to comment at this time. If you are a player, make sure to use the email you signed up with.</p>
                      :
                      <button id="signInButton" className="login-btn" type="submit" onClick={signIn}>Sign in to comment</button>
                    }

                    <button id="postCommentButton" className="login-btn hidden" type="submit" onClick={postComment}>Post</button>
                    <button id="cancelCommentButton" className="login-btn-secondary hidden" type="submit" onClick={cancelComment}>Cancel</button>
                  </div>
                  <div id='commentSpinner' className="spinner-border league-spinner-primary hidden" role="status"></div>
                </div>
              </div>

              <div id="allCommentsGroup">
                { displayComments() }
              </div>
            </div>
          </> : gameForfeited ?
          <>
            <div className="league-game-stats-container">
              <p className="league-title-skinny-small">Game Forfeited</p>
              <p>This game was forfeited by { losingTeam.name }.</p>
            </div>
          </> :
          <>
            <div className="league-game-stats-container">
              <p className="league-title-skinny-small">No Stats</p>
              <p>There were either no stats collected for this game or they are pending scorebook entry.</p>
            </div>
          </>
        }
      </>
    )
  }

  function getTimeAgo(timestamp) {
    return utils.getTimeAgo(timestamp);
  }

  function signIn () {
    navigate(`/login?redirect=games/${gameID}`)
  }

  // to remove...
  function getStatsForPlayer (playerID, season, withStealsAndBlocks) {
    let allStatsForPlayer;
    if (withStealsAndBlocks) {
      allStatsForPlayer = allGameStats.filter((gameStat) => gameStat.playerID == playerID && utils.getGameForID(allGames, gameStat.gameID).date > DATE_WHEN_BLOCKS_AND_STEALS_WERE_ADDED && gameStat.status == 'played')
    } else {
      allStatsForPlayer = allGameStats.filter((gameStat) => gameStat.playerID == playerID && gameStat.status == 'played')
    }

    if (season == 0) {
      return allStatsForPlayer;
    } else {
      return allStatsForPlayer.filter((stat) => utils.getGameForID(allGames, stat.gameID).season == season)
    }
  }

  function showTopSeasonStats (game, awayTeam, homeTeam, statLabel, season = 0) {
    let playerIDsInGame = [...awayTeam.players, ...homeTeam.players]
    let playersInGame = playerIDsInGame.map((playerID) => utils.getPlayerByID(players, playerID))

    // let seasonStats = {};
    let playerAverages = []
    playersInGame.forEach((p) => {
      let allStatsForPlayer;
      if (statLabel == 'blocks' || statLabel == 'steals') {
        allStatsForPlayer = getStatsForPlayer(p.id, season, true);
      } else {
        allStatsForPlayer = getStatsForPlayer(p.id, season, false);
      }

      if (allStatsForPlayer.length == 0) {
        return;
      }

      let average = allStatsForPlayer.reduce(function (avg, player, _, { length }) {
        return avg + player[statLabel] / length;
      }, 0);

      let playerTeam = utils.getCurrentTeamForPlayerID(teams, p.id)

      let playerAverage = {
        'playerID': p.id,
        'name': utils.displayPlayerName(p),
        'team': playerTeam.name,
        'average': average
      };
      if (!isNaN(average)) {
        playerAverages.push(playerAverage)
      }
      // seasonStats[p.id] = allStatsForPlayer;
    })

    let topThreePlayerAverages = playerAverages.sort((function(a,b){
      return a.average > b.average ? -1 : 1;
    })).slice(0, 3);

    let topFivePlayerAverages = playerAverages.sort((function(a,b){
      return a.average > b.average ? -1 : 1;
    })).slice(0, 5);

    let statLabelMap = {
      'assists': 'Assists',
      'points': 'Points',
      'rebounds': 'Rebounds',
      'steals': 'Steals',
      'blocks': 'Blocks'
    }

    // Choose between top 3 or top 5 players to show
    let topPlayerAverages = topThreePlayerAverages;
    // let topPlayerAverages = topFivePlayerAverages;

    if (topPlayerAverages.length == 0) {
      return;
    } else {
      return (
        <div className='league-player-display'>
          <p className="league-title-standard-small text-center"><b>{ statLabelMap[statLabel] }</b></p>
          <div className='league-player-container'>
            <div className='league-game-player-stats'>
              { topPlayerAverages.map((player) => showAverageStatRow(player, statLabel) ) }
            </div>
          </div>
        </div>
      )
    }
  }

  function showTopStats (statList, statLabel) {
    let statLabelMap = {
      'assists': 'Assists',
      'points': 'Points',
      'rebounds': 'Rebounds',
      'steals': 'Steals',
      'blocks': 'Blocks'
    }

    // Remove the players if they did not record the stat
    statList = statList.filter((gameStat) => gameStat[statLabel])

    let topThreePlayerStats = statList.sort((function(a,b){
      let a_stat = a[statLabel];
      if (a_stat == '') {
        a_stat = -1
      }

      let b_stat = b[statLabel];
      if (b_stat == '') {
        b_stat = -1
      }
      return a_stat > b_stat ? -1 : 1;
    })).slice(0, 3);

    let topFivePlayerStats = statList.sort((function(a,b){
      let a_stat = a[statLabel];
      if (a_stat == '') {
        a_stat = -1
      }

      let b_stat = b[statLabel];
      if (b_stat == '') {
        b_stat = -1
      }
      return a_stat > b_stat ? -1 : 1;
    })).slice(0, 5);

    // Choose between top 3 or top 5 players to show
    let topPlayerStats = topThreePlayerStats;
    // let topPlayerStats = topFivePlayerStats;

    if (topPlayerStats.length == 0) {
      return;
    } else {
      return (
        <div className='league-player-display'>
          <p className="league-title-standard-small text-center"><b>{ statLabelMap[statLabel] }</b></p>
          <div className='league-player-container'>
            <div className='league-game-player-stats'>
              { topPlayerStats.map((playerStat) => showStatRow(playerStat, statLabel) ) }
            </div>
          </div>
        </div>
      )
    }
  }

  function showAverageStatRow(player, stat) {
    let statLabelAbbv = {
      'assists': 'APG',
      'points': 'PPG',
      'rebounds': 'RPG',
      'steals': 'SPG',
      'blocks': 'BPG'
    }

    let p = utils.getPlayerByID(players, player.playerID)
    let playerTeam = utils.getCurrentTeamForPlayerID(teams, player.playerID)
    return (
      <div className='league-game-player-stat-group'>
        <p className='league-game-player-name' key={player.id + '-player-name'}><a className="unstyled-link" href={"/players/" + player.playerID }>{ utils.displayPlayerName(p) }</a> <span className='league-season-average-label'>({ playerTeam?.name })</span></p>
        <p className='league-game-player-stat' key={player.id + '-player-stat'}>{ parseFloat(player.average.toFixed(1)) } <span className='league-season-average-label'>{ statLabelAbbv[stat] }</span></p>
      </div>
    )
  }

  function showStatRow(playerStat, stat) {
    let player = utils.getPlayerByID(players, playerStat.playerID)

    if (!player) {
      let playerID = playerStat.playerID
      let teamID = playerStat.teamID
      player = defaultPlayer;
      player['id'] = playerID
      player['firstName'] = playerID
      player['lastName'] = ''
      player['teamID'] = teamID
    }

    return (
      <div className='league-game-player-stat-group'>
        <p className='league-game-player-name' key={player.id + '-player-name'}><a className="unstyled-link" href={"/players/" + player.id }>{ utils.displayPlayerName(player) }</a> <span className='league-season-average-label'>({ utils.getTeamForID(teams, playerStat.teamID).name })</span></p>
        <p className='league-game-player-stat' key={player.id + '-player-stat'}>{ playerStat[stat] }</p>
      </div>
    )
  }

  if (dataReady == false) {
    return (
      <div className="Schedule">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="Game">
        <div className="league-content">

          <div className="league-body">
            <div className="league-mid-width-container">
              <div className="league-game-display">
                <div className="text-left">
                  { utils.playoffsStarted(league) ? <p className='league-game-back-button'><a className='league-link' href="/"><b>← Back to Bracket</b></a></p> : '' }
                  <p className='league-game-back-button'><a className='league-link' href="/schedule"><b>← View All Games</b></a></p>
                  <div className="dropdown league-dropdown">
                  </div>
                </div>
                <div className="text-right">
                  { showEditOption() }
                </div>
              </div>
            </div>
            <Announcement />
            <div className="schedule">
              { displayGame() }
            </div>
          </div>

        </div>

        <Footer/>
      </div>
    );
  }
}

export default Game;
